import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import LangSwitcher from "@/components/LangSwitcher";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useState } from "react";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/products?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#FBF9F6]/85 backdrop-blur-xl border-b border-[#E2DCD0]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-full bg-[#E25A24] grid place-items-center text-white font-serif text-xl">स</div>
          <div>
            <div className="font-serif text-xl leading-none text-[#13294B]">Swadeshi Bazaar</div>
            <div className="label-caps mt-0.5">{t("Made in India")}</div>
          </div>
        </Link>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#718096]" />
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search handloom, spices, pottery…")}
            className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-[#E2DCD0] focus:outline-none focus:border-[#E25A24] text-sm"
          />
        </form>

        <nav className="ml-auto flex items-center gap-2">
          <Link to="/genz" data-testid="nav-genz" className="hidden md:inline px-3 py-2 text-sm font-bold text-[#E25A24] hover:text-[#B44618] transition-colors">{t("Gen Z")}</Link>
          <Link to="/genalpha" data-testid="nav-genalpha" className="hidden md:inline px-3 py-2 text-sm font-bold text-[#13294B] hover:text-[#0D141C] transition-colors">{t("Gen Alpha")}</Link>
          <Link to="/vendor/apply" data-testid="nav-sell" className="hidden md:inline px-3 py-2 text-sm font-semibold text-[#13294B] hover:text-[#E25A24] transition-colors">{t("Sell with us")}</Link>
          <LangSwitcher />
          <Link to="/cart" data-testid="nav-cart" className="relative p-2 rounded-full hover:bg-[#F2EFEB] transition-colors">
            <ShoppingBag className="w-5 h-5 text-[#13294B]" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#E25A24] text-white text-[10px] font-bold grid place-items-center">{count}</span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="p-2 rounded-full hover:bg-[#F2EFEB] transition-colors">
                  <User className="w-5 h-5 text-[#13294B]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-[#0D141C]">{user.name}</div>
                  <div className="text-xs text-[#718096]">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/orders")}>
                  <ShoppingBag className="w-4 h-4 mr-2" /> {t("My Orders")}
                </DropdownMenuItem>
                {user.role === "seller" && (
                  <DropdownMenuItem data-testid="menu-vendor" onClick={() => navigate("/vendor/dashboard")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t("Vendor Dashboard")}
                  </DropdownMenuItem>
                )}
                {user.role === "buyer" && (
                  <DropdownMenuItem data-testid="menu-become-seller" onClick={() => navigate("/vendor/apply")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t("Become a Seller")}
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <ShieldCheck className="w-4 h-4 mr-2" /> {t("Admin Console")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> {t("Sign out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" data-testid="nav-login" className="btn-primary text-sm !py-2 !px-5">{t("Sign in")}</Link>
          )}
        </nav>
      </div>
    </header>
  );
}
